import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Typography from "@material-ui/core/Typography";
import {
  selectAreAllPagesFetched,
  selectGetProductIds,
  selectGetProductListForFindProductsModal,
  selectIsFetchingProductIds,
  selectIsProductIdsFetched,
} from "../../store/find-products/FindProductSelectors";
import { ProductSequenceData } from "../../store/product-list/ProductListTypes";
import { Skeleton } from "@mui/material";
import styled from "styled-components";
import { CircularProgress } from "@material-ui/core";
import { useIntl } from "react-intl";
import {
  selectChildCategories,
  selectTopCategories,
  selectCurrentCategory,
} from "../../store/category/CategorySelectors";
import { selectCurrentStoreId } from "../../store/store-list/StoreListSelectors";
import { selectCurrentCatalogId } from "../../store/catalog/CatalogSelectors";
import {
  updateProductAddCategoryRequestAction,
  updateProductRemoveCategoryRequestAction,
} from "../../store/product/ProductActions";
import {
  unLockProduct,
  setPinnedProducts,
} from "../../store/product-list/ProductListActions";
import {
  selectLockedProducts,
  selectPinnedProductIds,
} from "../../store/product-list/ProductListSelectors";
import { selectAdapterInfo } from "store/adapter-config/AdapterConfigSelectors";
import { AdapterInfoObject } from "utils/AppConfigUtils";
import CheckboxComponent from "../common/Checkbox";
import { NoSearchResultIcon } from "../../assets";
import { selectProductByIdForFindProducts } from "store/find-products/FindProductSelectors";
import { selectProductImagePrefix } from "store/app-config/AppConfigSelectors";
import AppState from "store/AppState";
import { isAbsolutePath } from "utils/ProductUtil";
import { selectAllProductsFetched } from "store/product/ProductSelectors";

const useStyles = makeStyles((theme) => ({
  formControl: {
    height: "90%",
    overflow: "auto",
    padding: "1rem",
  },
  img: {
    width: "80%",
    height: "225px",
    objectFit: "contain",
    display: "block",
    margin: "0 auto",
  },
  imgContainer: {
    width: "100%",
    height: "225px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    // maxWidth: '200px',
    // maxHeight: '300px',
  },
  formControlLabel: {
    width: "100%",
    overflow: "hidden",
    margin: 0,
    padding: "0.85rem",
    position: "relative",
    "&>.MuiFormControlLabel-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      textAlign: "center",
      width: "100%",
    },
    "&>.MuiButtonBase-root": {
      position: "absolute",
      top: 0,
      right: 0,
    },
  },
  typographyBody1: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "14px",
  },
  partNumber: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "0.8rem",
    color: theme.palette.text.primary,
    marginTop: "5px",
  },
  checkBox: {
    margin: "5px",
    padding: "5px 8px",
  },
  circularProgress: {
    marginRight: "8px",
  },
  categoryLabel: {
    fontSize: "0.75rem",
  },
  noResultDiv: { textAlign: "center", width: "100%" },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

interface Props {
  currentModalSelectedCategory: string | null;
  productsToBeAdded: {};
  setProductsToBeAdded: React.Dispatch<React.SetStateAction<{}>>;
  productsToBeRemoved: {};
  setProductsToBeRemoved: React.Dispatch<React.SetStateAction<{}>>;
  existingAddingRemoveProductIdsOperation?: string[];
}

const StyledProductList = styled.div`
  height: 100%;
  margin-top: 10px;
  width: 100%;
`;

const productListLoading = (count) => {
  return (
    count > 0 && (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          rowGap: "10px",
        }}
      >
        {[...Array(count)].map(() => (
          <Skeleton width="33%" height={330} variant="rectangular" />
        ))}
      </div>
    )
  );
};

const FindProductsProductList: React.FC<Props> = (props) => {
  const classes = useStyles();
  const intl = useIntl();
  const {
    currentModalSelectedCategory,
    existingAddingRemoveProductIdsOperation,
  } = props;
  const productList = useSelector(selectGetProductListForFindProductsModal);
  const isAllPagesFetched = useSelector(selectAreAllPagesFetched);
  const productIds = useSelector(selectGetProductIds);
  const isFetchingProductIds = useSelector(selectIsFetchingProductIds);
  const isProductIdsFetched = useSelector(selectIsProductIdsFetched);
  const allProducts = useSelector(selectAllProductsFetched);

  const topCategoriesMainSequence = useSelector(selectTopCategories);
  const childCategoriesMainSequence = useSelector(selectChildCategories);

  const currentStoreId = useSelector(selectCurrentStoreId);
  const currentCategoryId = useSelector(selectCurrentCategory);
  const catalogId = useSelector(selectCurrentCatalogId);
  const adapterInfo: AdapterInfoObject = useSelector((state: AppState) =>
    selectAdapterInfo(state),
  );
  const unmanagedSupported = adapterInfo?.capabilities?.includes(
    "unmanaged-products",
  )
    ? "unmanaged"
    : "manual";

  const [checkedProductIds, setCheckedProductIds] = useState([] as string[]);

  const productById = useSelector(selectProductByIdForFindProducts);
  const productImagePrefix = useSelector((state: AppState) =>
    selectProductImagePrefix(state, currentStoreId),
  );
  const pinnedProductIds = useSelector(selectPinnedProductIds);
  const lockedProductsInCategory = useSelector((state: AppState) =>
    selectLockedProducts(state, currentCategoryId, catalogId),
  );

  const dispatch = useDispatch();

  // Check if products are in the current category (from sequence tab)
  useEffect(() => {
    if (productList.length && currentCategoryId && Object.keys(allProducts).length > 0) {
      const currentCategoryProductIdsSet = new Set(Object.keys(allProducts));
      const productIdsInCurrentCategory = productList
        .filter((product) => currentCategoryProductIdsSet.has(product.productId))
        .map((product) => product.productId);
      setCheckedProductIds(productIdsInCurrentCategory);
    } else if (!productList.length || !currentCategoryId) {
      // Reset when productList is empty or no current category
      setCheckedProductIds([]);
    }
  }, [productList, currentCategoryId, allProducts]);

  const addRemoveFromCurrentCategory = useCallback(
    (
      productId: string | undefined,
      currentCategoryId: string,
      e: any,
    ) => {
      const tc = topCategoriesMainSequence.find(
        (e) => e.categoryId === currentCategoryId,
      );
      const ccFilter = Object.entries(childCategoriesMainSequence).find((e) =>
        e[1].childCategories.find((el) => el.categoryId === currentCategoryId),
      );
      const payload = {
        sequence: 0,
        productId,
        categoryId: currentCategoryId,
        storeId: currentStoreId,
        parentId: !tc && ccFilter ? ccFilter[0] : null,
        isFromFindProductsModal: true,
      };
      if (e.target.checked) {
        dispatch(
          updateProductAddCategoryRequestAction(payload),
        );
      } else {
        // When unchecking, check if product has locks or pins and remove them
        if ( productId) {
          // Check if product is locked in the category
          const lockedProduct = lockedProductsInCategory?.find(
            (lock) => lock.productId === productId,
          );
          
          // Check if product is pinned
          const isPinned = pinnedProductIds?.includes(productId);
          
          // Remove lock if product is locked
          if (lockedProduct) {
            dispatch(
              unLockProduct({
                unlockedProducts: [lockedProduct],
              }),
            );
          }
          
          // Remove pin if product is pinned
          if (isPinned && pinnedProductIds) {
            const updatedPinnedProducts = pinnedProductIds.filter(
              (id) => id !== productId,
            );
            dispatch(
              setPinnedProducts({
                pinnedProductIds: updatedPinnedProducts,
                sortOption: unmanagedSupported,
                catalogId: catalogId,
                categoryId: currentCategoryId,
              }),
            );
          }
        }
        
        dispatch(updateProductRemoveCategoryRequestAction(payload));
      }
    },
    [
      topCategoriesMainSequence,
      childCategoriesMainSequence,
      currentStoreId,
      catalogId,
      unmanagedSupported,
      lockedProductsInCategory,
      pinnedProductIds,
      dispatch,
    ],
  );

  const renderLabel = useCallback(
    (item: ProductSequenceData, isLoading: boolean) => {
      const productName = productById[item.productId]?.name;
      const productThumbnail = productById[item.productId]?.thumbnail;
      const thumbnail = productThumbnail
        ? isAbsolutePath(productThumbnail)
          ? productThumbnail
          : productImagePrefix + productThumbnail
        : null;

      const productCode = productById[item.productId]?.code || "";
      const productId = item.productId;

      return (
        <>
          <div className={classes.imgContainer}>
            {thumbnail ? (
              <img src={thumbnail} alt={productName} className={classes.img} />
            ) : null}
          </div>
          <Typography
            className={classes.typographyBody1}
            classes={{
              root: classes.typographyBody1,
            }}
            noWrap
            title={productName}
          >
            {productName}
          </Typography>
          <Typography
            className={classes.partNumber}
            classes={{
              root: classes.typographyBody1,
            }}
            noWrap
            title={productCode}
          >
            {productCode}
          </Typography>
          {currentCategoryId ? (
            isLoading ? (
              <FormControlLabel
                className={classes.checkBox}
                control={<CircularProgress size={20} className={classes.circularProgress} />}
                label={<p className={classes.categoryLabel}>In Category</p>}
              />
            ) : (
              <FormControlLabel
                className={classes.checkBox}
                control={
                  <CheckboxComponent
                    checkboxId="FindProductsProductList"
                    checked={
                      checkedProductIds.findIndex((id) => id === productId) !== -1
                    }
                    value={productId}
                    setCheckedProductIds={setCheckedProductIds}
                    currentCategoryId={currentCategoryId || ""}
                    addRemoveFromCurrentCategory={addRemoveFromCurrentCategory}
                    handleParam={productId}
                  />
                }
                label={<p className={classes.categoryLabel}>In Category</p>}
              />
            )
          ) : null}
        </>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentCategoryId, addRemoveFromCurrentCategory],
  );
  return (
    <StyledProductList>
      <Grid container spacing={2} direction="row">
        {productList &&
          productList.length > 0 &&
          productList.map((item, index) => {
            const isLoadingState =
              existingAddingRemoveProductIdsOperation?.length
                ? existingAddingRemoveProductIdsOperation.findIndex(
                    (id) => id === item.productId,
                  ) !== -1
                : false;

            return (
              <Grid item sm={3} key={`FindProductsProductsList_${index}`}>
                <Paper className={classes.paper}>
                  <FormControlLabel
                    className={classes.formControlLabel}
                    control={<></>}
                    label={renderLabel(item, isLoadingState)}
                  />
                </Paper>
              </Grid>
            );
          })}
      </Grid>
      {!isAllPagesFetched
        ? productListLoading(
            productList.length === 0
              ? productIds.length
              : productIds.length - productList.length,
          )
        : null}
      {isAllPagesFetched &&
        productList.length === 0 &&
        (currentModalSelectedCategory !== "" || isProductIdsFetched) && (
          <div className={classes.noResultDiv}>
            {/* // <Box position="absolute" top="50%" left="45%"> */}
            <NoSearchResultIcon />
            <Typography variant="h5">
              {intl.formatMessage({
                id: "productListSwitch.noProductsFoundText",
                defaultMessage: "No Products Available",
              })}
            </Typography>
          </div>
          //  </Box>
        )}
      {productIds.length === 0 &&
        ((currentModalSelectedCategory !== "" &&
        !isAllPagesFetched) || (isFetchingProductIds)) && (
          <CircularProgress size={24} className={classes.buttonProgress} />
        )}
    </StyledProductList>
  );
};

export default FindProductsProductList;
